import { FC } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { Add, Delete, Pencil } from '@factorialco/f0-react/icons/app'
import { PerformanceGoalsMetricGroupCrumbQuery } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { useDeleteMetricGoal } from 'modules/performance_goals/hooks/deleteMetricGroup'

type Props = {
  metricGroup: PerformanceGoalsMetricGroupCrumbQuery['goals']['metricGroup']
}

export const GoalHeader: FC<Props> = ({ metricGroup }) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [_searchParams, setSearchParams] = useSearchParams()

  // TODO once we remove ResponsivePage's header, move CreateMetricModal and UpdateMetricGroupModal from MetricGroupPage to this component
  const openCreateDialog = () => setSearchParams({ modal: 'create' })
  const openEditDialog = () => setSearchParams({ modal: 'edit' })

  const { handleDelete } = useDeleteMetricGoal()

  const onDelete = async () => {
    if (!metricGroup) return

    await handleDelete(metricGroup, () => {
      navigate({
        pathname: navigation.performanceGoals.groups.fullPath(),
      })
    })
  }

  const performanceCycle = metricGroup?.performanceCycles?.[0]
  const canUpdateGoal = metricGroup?._policies?.update.can
  const canCreateGoalMetric = metricGroup?._policies?.createMetric.can
  const canDeleteGoal = metricGroup?._policies?.delete.can

  return (
    <ResourceHeader
      title={metricGroup?.title || ''}
      description={metricGroup?.description || ''}
      status={
        performanceCycle && {
          label: i18n.t('performance_cycles.cycle_filter.label'),
          text: performanceCycle.title,
          variant: 'neutral',
        }
      }
      primaryAction={
        canCreateGoalMetric
          ? {
              label: i18n.t('performance_goals.pages.metric_group_page.create_key_result_button'),
              icon: Add,
              onClick: openCreateDialog,
            }
          : undefined
      }
      otherActions={[
        canUpdateGoal && {
          label: i18n.t('common.edit'),
          icon: Pencil,
          onClick: openEditDialog,
        },
        canDeleteGoal && {
          label: i18n.t('common.delete'),
          variant: 'critical',
          icon: Delete,
          onClick: onDelete,
        },
      ].filter(Boolean)}
    />
  )
}
